import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { ArrowLeft, Loader2, ChevronRight } from "lucide-react";
import { formatDate } from "@/lib/format";
import clsx from "clsx";
import {
  ACTION_RUN_STATUS_LABELS,
  ACTION_TYPE_LABELS,
  ENROLLMENT_STATUS_LABELS,
  STEP_RUN_STATUS_LABELS,
} from "./labels";

interface Enrollment {
  id: string;
  subjectType: string;
  subjectId: string;
  status: "active" | "completed" | "cancelled" | "failed";
  currentStepIndex: number;
  nextRunAt: string | null;
  enteredAt: string;
  completedAt: string | null;
  lastError: string | null;
}

interface StepRun {
  id: string;
  stepIndex: number;
  status: "completed" | "skipped" | "failed";
  actions: Array<{ type: string; status: string; reason?: string; error?: string }> | null;
  error: string | null;
  ranAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

function RunList({ enrollmentId }: { enrollmentId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["enrollment-runs", enrollmentId],
    queryFn: () =>
      api.get<ApiResponse<StepRun[]>>(`/api/admin/journeys/enrollments/${enrollmentId}/runs`),
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin text-gray-400" />;
  const runs = data?.data ?? [];
  if (runs.length === 0)
    return <p className="text-xs text-gray-400">{t("marketing.journeyEnrollments.noRuns")}</p>;
  return (
    <div className="space-y-1">
      {runs.map((r) => (
        <div key={r.id} className="rounded border border-gray-200 p-2 text-xs dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {t("marketing.journeyStep.stepTitle", { n: r.stepIndex + 1 })} —{" "}
              <span
                className={
                  r.status === "failed"
                    ? "text-red-600"
                    : r.status === "skipped"
                      ? "text-amber-600"
                      : "text-green-600"
                }
              >
                {STEP_RUN_STATUS_LABELS[r.status]}
              </span>
            </span>
            <span className="text-gray-400">{formatDate(r.ranAt)}</span>
          </div>
          {r.actions && r.actions.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-gray-500">
              {r.actions.map((a, i) => (
                <li key={i}>
                  {(ACTION_TYPE_LABELS as Record<string, string>)[a.type] ?? a.type}:{" "}
                  <span
                    className={
                      a.status === "failed"
                        ? "text-red-600"
                        : a.status === "skipped"
                          ? "text-amber-600"
                          : "text-green-600"
                    }
                  >
                    {ACTION_RUN_STATUS_LABELS[a.status] ?? a.status}
                  </span>
                  {(a.reason || a.error) && (
                    <span className="text-gray-400"> — {a.reason || a.error}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export default function JourneyEnrollmentsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["journey-enrollments", id],
    queryFn: () => api.get<ApiResponse<Enrollment[]>>(`/api/admin/journeys/${id}/enrollments`),
  });

  const enrollments = data?.data ?? [];

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex items-center gap-3">
        <Link
          to={`/journeys/${id}`}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {t("marketing.journeyEnrollments.title")}
        </h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : enrollments.length === 0 ? (
        <p className="py-16 text-center text-gray-400">{t("marketing.journeyEnrollments.empty")}</p>
      ) : (
        <div className="space-y-2">
          {enrollments.map((e) => (
            <div
              key={e.id}
              className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
            >
              <button
                onClick={() => setOpen(open === e.id ? null : e.id)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <ChevronRight
                    className={clsx(
                      "h-4 w-4 text-gray-400 transition-transform",
                      open === e.id && "rotate-90",
                    )}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {e.subjectType}:{e.subjectId}
                    </div>
                    <div className="text-xs text-gray-400">
                      {t("marketing.journeyEnrollments.meta", {
                        step: e.currentStepIndex + 1,
                        date: formatDate(e.enteredAt),
                      })}
                      {e.nextRunAt &&
                        ` ${t("marketing.journeyEnrollments.metaNext", { date: formatDate(e.nextRunAt) })}`}
                    </div>
                  </div>
                </div>
                <span className={clsx("rounded-full px-2 py-0.5 text-xs", STATUS_COLORS[e.status])}>
                  {ENROLLMENT_STATUS_LABELS[e.status]}
                </span>
              </button>
              {open === e.id && (
                <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
                  {e.lastError && <p className="mb-2 text-xs text-red-500">{e.lastError}</p>}
                  <RunList enrollmentId={e.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
